import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Radio } from "lucide-react";
import { Reveal } from "../components/motion";
import { PageHero, CtaBand, FaqList } from "../components/shared";
import { QuoteLink } from "../components/QuotePanel";
import { imgProps } from "../components/media";
import { PI_STEPS, PI_INCLUDES, FAQS, img, gruppo, CONTACT } from "../data/site";

const VIDEO_SALA = { src: "/assets/video/loop-sala-operativa-1080.mp4", poster: "/assets/video/loop-sala-operativa-poster.jpg" };

const STEP_DUR = 5;

export default function CentraleOperativa() {
  const [step, setStep] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setStep((s) => (s + 1) % PI_STEPS.length), STEP_DUR * 1000);
    return () => clearInterval(t);
  }, []);
  const gallery = [
    ["sala-operativa-videowall.jpg", "Videowall della sala operativa"],
    ["sala-operativa-pareti-video.jpg", "Pareti video con le telecamere collegate"],
    ["sala-operativa-operatore.jpg", "Operatore al lavoro in centrale"],
    ["sala-operativa-monitor.jpg", "Monitor di controllo allarmi e telecamere"],
  ];
  return (
    <>
      <PageHero
        kick="Centrale operativa e pronto intervento"
        lines={["Quando scatta l'allarme,", "non resti mai solo."]}
        sub="Due centrali operative certificate UNI CEI EN 50518 e guardie giurate armate pronte a intervenire, giorno e notte, 365 giorni l'anno."
        ph={img("sala-operativa-panoramica.jpg")}
        video={VIDEO_SALA}
        ctas={
          <>
            <QuoteLink servizio="centrale-operativa" className="btn" testid="co-hero-cta">
              Collega il tuo allarme
            </QuoteLink>
            <a href="#come-funziona" className="btn ghost" data-testid="co-hero-cta2">
              Pronto intervento
            </a>
          </>
        }
      />

      <section className="sec" id="come-funziona" data-testid="co-steps">
        <div className="wrap">
          <Reveal className="sec-head">
            <p className="kick">Pronto intervento</p>
            <h2 className="h2">Cosa succede, un passo alla volta</h2>
          </Reveal>
          <div className="steps">
            {PI_STEPS.map(([t, d], i) => (
              <button
                type="button"
                key={t}
                className={`step ${step === i ? "on" : ""}`}
                onClick={() => setStep(i)}
                data-testid={`co-step-${i}`}
                aria-current={step === i}
              >
                <span className="bar" aria-hidden="true">
                  <span className="fill" style={{ "--dur": `${STEP_DUR}s`, animationPlayState: step === i ? "running" : "paused" }} />
                </span>
                <span className="n">{`0${i + 1}`}</span>
                <span className="st">{t}</span>
                <span className="d">{d}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="sec-mist sec" data-testid="co-sale">
        <div className="wrap">
          <div style={{ display: "flex", flexWrap: "wrap", gap: "48px 80px", alignItems: "center" }}>
            <Reveal style={{ flex: "1 1 440px", minWidth: 0 }}>
              <div className="phframe tall">
                <img {...imgProps(img("sala-operativa-team.jpg"), { sizes: "(max-width: 760px) 92vw, 520px" })} alt="L'equipe della centrale operativa" />
                <span className="cap">L'equipe in sala operativa</span>
              </div>
            </Reveal>
            <Reveal delay={0.08} style={{ flex: "1 1 480px", minWidth: 0 }}>
              <p className="kick">Due sale operative</p>
              <h2 className="h2">Due sale operative, sempre collegate.</h2>
              <p className="lead" style={{ margin: "24px 0 30px" }}>
                Le sale operative di San Cesareo (Roma) e Bascapè (Pavia) sono in collegamento costante tra loro e certificate UNI CEI EN 50518. Ognuna è il backup dell'altra: se una sede si ferma, l'altra subentra subito e il servizio non si interrompe.
              </p>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(2,minmax(0,1fr))", gap: 14 }}>
                {[
                  ["Centro-Sud", "San Cesareo", "Via Prusst snc, Roma"],
                  ["Nord", "Bascapè", "Via Crivelli 9, Pavia"],
                ].map(([k, c, a]) => (
                  <div key={c} className="mini" data-testid={`co-sala-${c.toLowerCase()}`}>
                    <p className="k" style={{ fontSize: 13, fontWeight: 700, letterSpacing: ".14em", textTransform: "uppercase", color: "var(--blue)", margin: "0 0 8px" }}>
                      {k}
                    </p>
                    <h3>{c}</h3>
                    <p>{a}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
          <div className="pgallery" data-testid="co-gallery">
            {gallery.map(([f, alt], i) => (
              <Reveal key={f} delay={i * 0.06}>
                <div className="phframe">
                  <img {...imgProps(img(f), { sizes: "(max-width: 760px) 92vw, 440px" })} alt={alt} />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="sec" data-testid="co-includes">
        <div className="wrap">
          <Reveal className="sec-head">
            <p className="kick">Il servizio</p>
            <h2 className="h2">Cosa comprende</h2>
          </Reveal>
          <div className="minis">
            {PI_INCLUDES.map(([t, d], i) => (
              <Reveal key={t} delay={i * 0.05}>
                <div className="mini" data-testid={`co-include-${i}`}>
                  <div className="mic" style={{ background: "var(--mist)", color: "var(--blue)" }}>
                    <Radio size={20} aria-hidden="true" />
                  </div>
                  <h3>{t}</h3>
                  <p>{d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="sec" style={{ paddingTop: 0 }} data-testid="co-axentra">
        <div className="wrap">
          <Reveal>
            <div className="tl-card" style={{ background: "var(--navy)", border: 0, display: "flex", flexWrap: "wrap", gap: 32, alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ maxWidth: 720 }}>
                <img {...imgProps(gruppo("axentra-scuro.png"), { sizes: "220px" })} alt="Axentra" style={{ height: 44, width: "auto", marginBottom: 18 }} />
                <h3 style={{ color: "#fff" }}>Non hai ancora un impianto?</h3>
                <p>
                  Lo progetta e installa Axentra, l'azienda tecnologica del gruppo: allarmi e videosorveglianza con intelligenza artificiale, già pronti per la nostra centrale.
                </p>
              </div>
              <a href={CONTACT.axentraUrl} className="btn" target="_blank" rel="noreferrer" data-testid="co-axentra-cta">
                Vai ad Axentra
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="sec" style={{ paddingTop: 0 }} data-testid="co-faq">
        <div className="wrap" style={{ display: "flex", flexWrap: "wrap", gap: "40px 90px" }}>
          <Reveal style={{ flex: "1 1 360px" }}>
            <p className="kick">Domande frequenti</p>
            <h2 className="h2">Domande sul pronto intervento</h2>
          </Reveal>
          <Reveal delay={0.1} style={{ flex: "2 1 560px", minWidth: 0 }}>
            <FaqList items={FAQS.centrale} testid="co-faq" />
          </Reveal>
        </div>
      </section>

      <CtaBand testid="co-cta" />
    </>
  );
}
